import { useEffect, useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { useStore, Deal, Workspace, Project, ProjectMember } from '@/store/useStore';
import { toast } from 'sonner';

// ─── Supabase join shapes ───
interface WorkspaceMemberRow {
  id: string;
  user_id: string;
  role: string;
  profiles: { id: string; name: string; avatar_url: string | null } | null;
}

interface ProjectMemberRow {
  id: string;
  user_id: string | null;
  /** A coluna é `project_role` — é o que o select pede. `role` não existe. */
  project_role: string | null;
  profiles: { id: string; name: string | null; avatar_url: string | null } | null;
}

export interface ProjectRow {
  id: string;
  name: string;
  description: string | null;
  workspace_id: string;
  created_at: string;
  project_members: ProjectMemberRow[];
  [key: string]: unknown;
}

export interface WorkspaceRow {
  id: string;
  name: string;
  description: string | null;
  industry: string | null;
  website: string | null;
  logo_url: string | null;
  owner_id: string;
  invite_code: string | null;
  created_at: string;
  workspace_members: WorkspaceMemberRow[];
  projects: ProjectRow[];
}

export interface DbLead {
  id: string;
  project_id: string;
  company: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  priority: string | null;
  value: number | null;
  cash_collect: number | null;
  mrr: number | null;
  contract_value: number | null;
  column_id: string | null;
  days_in_column: number | null;
  origin: string | null;
  service: string | null;
  score: number | null;
  tags: string[] | null;
  notes: string | null;
  custom_fields: Record<string, unknown> | null;
  timeline: { date: string; action: string; user: string }[] | null;
  contact_logs: import('@/store/types').ContactLog[] | null;
  status_history: import('@/store/types').StatusHistoryEntry[] | null;
  responsible: string | null;
  sdr: string | null;
  closer: string | null;
  /** IDs (profile ou agent) — mantidos em sincronia com os nomes pelo trigger `sync_lead_people`. */
  sdr_id?: string | null;
  closer_id?: string | null;
  client_category: string | null;
  proposal_attached: boolean | null;
  proposal_attachments: import('@/store/types').ProposalAttachment[] | null;
  porte: string | null;
  contact_avatar: string | null;
  company_logo: string | null;
  company_info: import('@/store/types').CompanyInfo | null;
  contact_info: import('@/store/types').ContactInfo | null;
  additional_contacts: import('@/store/types').ContactInfo[] | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_content: string | null;
  utm_term: string | null;
  last_status_date: string | null;
  last_contact_at?: string | null;
  deleted_at: string | null;
  created_at: string;
}

// ─── Profile types ───
export interface Profile {
  id: string;
  name: string;
  email: string;
  avatar_url: string | null;
  role: string;
  tag: string | null;
  bio: string | null;
  department: string | null;
  seniority: string | null;
  last_seen_at: string | null;
  created_at: string;
  updated_at: string;
  full_name: string | null;
  birth_date: string | null;
  address: string | null;
  phone: string | null;
}

// ─── Profile ───
export function useProfile() {
  const queryClient = useQueryClient();

  const { data: profile = null, isLoading: loading, refetch } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) return null;
      const { data } = await supabase.from('profiles').select('*').eq('id', user.id).maybeSingle();
      
      if (data && !data.avatar_url) {
        const providerAvatarUrl = user.user_metadata?.avatar_url || user.user_metadata?.picture;
        if (providerAvatarUrl) {
          data.avatar_url = providerAvatarUrl;
          // Best-effort avatar sync — don't await, don't block, don't log errors
          // Uses setTimeout to ensure auth token is fully ready
          setTimeout(() => {
            supabase.from('profiles').update({ avatar_url: providerAvatarUrl }).eq('id', user.id).then(() => {});
          }, 2000);
        }
      }
      return data as Profile;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Real-time: invalidate profile when it changes in DB
  useEffect(() => {
    let userId: string | null = null;
    supabase.auth.getUser().then(({ data: { user } }) => { userId = user?.id ?? null; });

    // Append a per-mount random suffix so concurrent instances (StrictMode
    // double-mount, fast tab switch) don't collide on the same channel name —
    // supabase-js throws "cannot add postgres_changes callbacks ... after
    // subscribe()" if a channel with the same name is still tearing down.
    const channelName = `profile-realtime-${Math.random().toString(36).slice(2, 10)}`;
    const channel = supabase
      .channel(channelName)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'profiles',
      }, (payload) => {
        if (payload.new.id === userId) {
          queryClient.invalidateQueries({ queryKey: ['profile'] });
        }
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [queryClient]);

  const updateProfileMutation = useMutation({
    mutationFn: async (updates: Partial<Omit<Profile, 'id'>>) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      const { error } = await supabase.from('profiles').update({ ...updates, updated_at: new Date().toISOString() }).eq('id', user.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      toast.success('Perfil atualizado!');
    },
    onError: () => toast.error('Erro ao atualizar perfil')
  });

  return { profile, loading, updateProfile: updateProfileMutation.mutateAsync, refetch };
}

// ─── Workspaces ───
export function useWorkspaces() {
  const queryClient = useQueryClient();

  const { data: workspaces = [], isLoading: loading, refetch } = useQuery({
    queryKey: ['workspaces'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('workspaces')
        .select(`
          id, name, description, industry, website, logo_url, owner_id, invite_code, created_at,
          workspace_members(id, user_id, role, profiles(id, name, avatar_url)),
          projects(
            id, name, description, type, workspace_id, created_at,
            project_members(id, user_id, project_role, profiles(id, name, avatar_url))
          )
        `)
        .order('created_at', { ascending: false });

      if (error) { toast.error('Erro ao carregar workspaces'); throw error; }
      return data as WorkspaceRow[];
    },
    staleTime: 1000 * 60 * 5,
  });

  // Real-time: invalidate when workspace_members or projects change
  useEffect(() => {
    const rnd = Math.random().toString(36).slice(2, 8);
    const membersChannel = supabase
      .channel(`workspace-members-rt-${rnd}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'workspace_members',
      }, () => {
        queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      })
      .subscribe();

    const projectsChannel = supabase
      .channel(`workspace-projects-rt-${rnd}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'project_members',
      }, () => {
        queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      })
      .subscribe();

    return () => {
      supabase.removeChannel(membersChannel);
      supabase.removeChannel(projectsChannel);
    };
  }, [queryClient]);

  const createWorkspaceMutation = useMutation({
    mutationFn: async (ws: { name: string; description?: string; industry?: string; website?: string; logo_url?: string }) => {
      // Use SECURITY DEFINER RPC to avoid RLS + profiles FK constraint issue
      const { data, error } = await supabase.rpc('create_workspace_for_user', {
        p_name: ws.name,
        p_description: ws.description,
        p_industry: ws.industry,
        p_website: ws.website,
        p_logo_url: ws.logo_url,
      });
      if (error || !data) throw error ?? new Error('Failed to create workspace');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      toast.success('Workspace criado!');
    },
    onError: () => toast.error('Erro ao criar workspace')
  });

  const updateWorkspaceMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<{ name: string; description: string; industry: string; website: string; logo_url: string }> }) => {
      const { error } = await supabase.from('workspaces').update(updates).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['workspaces'] });
    },
    onError: () => toast.error('Erro ao atualizar workspace')
  });

  const deleteWorkspaceMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('workspaces').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      toast.success('Workspace excluído!');
    },
    onError: () => toast.error('Erro ao excluir workspace')
  });

  const joinWorkspaceMutation = useMutation({
    mutationFn: async (inviteCode: string) => {
      const { data: rawData, error } = await supabase.rpc('join_workspace_by_invite_code', {
        _invite_code: inviteCode,
      });

      if (error) throw error;
      const data = rawData as unknown as { success?: boolean; error?: string; already_member?: boolean; workspace_name?: string };

      if (data && !data.success) throw new Error(data.error || 'Erro ao entrar no workspace');
      return data;
    },
    onSuccess: (data) => {
      if (data?.already_member) {
        toast.info('Você já faz parte deste workspace');
      } else {
        queryClient.invalidateQueries({ queryKey: ['workspaces'] });
        toast.success(`Você entrou no workspace "${data?.workspace_name}"!`);
      }
    },
    onError: (e: any) => {
      const msg = e?.message || '';
      if (msg.includes('Could not find the function') || e?.code === 'PGRST202') {
        toast.error('Função de convite não configurada. Execute a migration SQL no Supabase.');
      } else {
        toast.error(msg || 'Erro ao entrar no workspace');
      }
    }
  });

  return { 
    workspaces, 
    loading, 
    createWorkspace: createWorkspaceMutation.mutateAsync, 
    updateWorkspace: (id: string, updates: any) => updateWorkspaceMutation.mutateAsync({ id, updates }), 
    deleteWorkspace: deleteWorkspaceMutation.mutateAsync, 
    joinWorkspace: async (code: string) => {
      try {
        await joinWorkspaceMutation.mutateAsync(code);
        return true;
      } catch {
        return false;
      }
    }, 
    refetch 
  };
}

// ─── Projects ───
export function useProjects(workspaceId: string | null) {
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchProjects = useCallback(async () => {
    if (!workspaceId) { setProjects([]); setLoading(false); return; }
    const { data, error } = await supabase
      .from('projects')
      .select(`id, name, description, workspace_id, created_at, project_members(id, user_id, project_role, profiles(id, name, avatar_url))`)
      .eq('workspace_id', workspaceId)
      .order('created_at', { ascending: false });
    if (error) { toast.error('Erro ao carregar projetos'); setLoading(false); return; }
    // name/workspace_id/created_at são nullable no schema (embora nenhuma
    // linha em produção tenha null). Um projeto sem nome é defeito de dado,
    // não um estado que a interface deva desenhar — então o valor é
    // resolvido AQUI, na fronteira de leitura, em vez de espalhar `?? ''`
    // por cada tela que mostra um projeto.
    setProjects((data || []).map(p => ({
      ...p,
      name: p.name ?? 'Projeto sem nome',
      workspace_id: p.workspace_id ?? workspaceId,
      created_at: p.created_at ?? new Date(0).toISOString(),
    })));
    setLoading(false);
  }, [workspaceId]);

  const createProject = useCallback(async (project: { name: string; description?: string }) => {
    if (!workspaceId) return null;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase.from('projects').insert({ ...project, workspace_id: workspaceId }).select().single();
    if (error) { toast.error('Erro ao criar projeto'); return null; }

    await supabase.from('project_members').insert({ project_id: data.id, user_id: user.id });
    await fetchProjects();
    toast.success('Projeto criado!');
    return data;
  }, [workspaceId, fetchProjects]);

  const deleteProject = useCallback(async (id: string) => {
    const { error } = await supabase.from('projects').delete().eq('id', id);
    if (error) { toast.error('Erro ao excluir projeto'); return; }
    await fetchProjects();
    toast.success('Projeto excluído!');
  }, [fetchProjects]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);
  return { projects, loading, createProject, deleteProject, refetch: fetchProjects };
}

// ─── Leads ───
export function useLeads(projectId: string | null) {
  const [leads, setLeads] = useState<DbLead[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchLeads = useCallback(async () => {
    if (!projectId) { setLeads([]); setLoading(false); return; }
    const { data, error } = await supabase
      .from('leads')
      .select('id, project_id, company, contact_name, email, phone, priority, value, cash_collect, mrr, contract_value, column_id, days_in_column, origin, service, score, tags, notes, custom_fields, timeline, contact_logs, status_history, responsible, sdr, closer, sdr_id, closer_id, client_category, proposal_attached, proposal_attachments, porte, contact_avatar, company_logo, company_info, contact_info, additional_contacts, utm_source, utm_medium, utm_content, utm_term, last_status_date, last_contact_at, deleted_at, created_at')
      .eq('project_id', projectId)
      .is('deleted_at', null)
      .order('created_at', { ascending: false });
    if (error) { toast.error('Erro ao carregar leads'); setLoading(false); return; }
    setLeads((data || []) as DbLead[]);
    setLoading(false);
  }, [projectId]);

  const createLead = useCallback(async (lead: Record<string, unknown>) => {
    if (!projectId) return null;
    const insertData = { ...lead, project_id: projectId } as Database['public']['Tables']['leads']['Insert'];
    const { data, error } = await supabase.from('leads').insert(insertData).select().single();
    if (error) { toast.error('Erro ao criar lead'); return null; }
    await fetchLeads();
    return data;
  }, [projectId, fetchLeads]);

  const createLeadsBulk = useCallback(async (leads: Record<string, unknown>[]) => {
    if (!projectId) return;
    const rows = leads.map(l => ({ ...l, project_id: projectId })) as Database['public']['Tables']['leads']['Insert'][];
    const { error } = await supabase.from('leads').insert(rows);
    if (error) { toast.error('Erro ao importar leads'); return; }
    await fetchLeads();
    toast.success(`${rows.length} leads importados!`);
  }, [projectId, fetchLeads]);

  const updateLead = useCallback(async (id: string, updates: Database['public']['Tables']['leads']['Update']) => {
    const { error } = await supabase.from('leads').update(updates).eq('id', id);
    if (error) { toast.error('Erro ao atualizar lead'); return; }
    await fetchLeads();
  }, [fetchLeads]);

  const deleteLead = useCallback(async (id: string) => {
    const { error } = await supabase.from('leads').update({ deleted_at: new Date().toISOString() }).eq('id', id);
    if (error) { toast.error('Erro ao excluir lead'); return; }
    await fetchLeads();
  }, [fetchLeads]);

  const deleteLeadsBulk = useCallback(async (ids: string[]) => {
    const { error } = await supabase.from('leads').update({ deleted_at: new Date().toISOString() }).in('id', ids);
    if (error) { toast.error('Erro ao excluir leads'); return; }
    await fetchLeads();
    toast.success(`${ids.length} leads excluídos!`);
  }, [fetchLeads]);

  // Envia apenas column_id ao DB — o trigger Postgres trg_track_lead_column_change
  // cuida automaticamente de status_history, timeline, days_in_column e last_status_date.
  // NÃO construir esses campos client-side para evitar conflito/duplicação com o trigger.
  const moveLeadColumn = useCallback(async (id: string, columnId: string) => {
    const lead = leads.find(l => l.id === id);
    if (!lead) return;
    await updateLead(id, { column_id: columnId });
  }, [leads, updateLead]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);
  return { leads, loading, createLead, createLeadsBulk, updateLead, deleteLead, deleteLeadsBulk, moveLeadColumn, refetch: fetchLeads };
}

// ─── Helper: Convert DB lead to store Deal format ───
export function dbLeadToDeal(lead: DbLead): Deal {
  const lastStatus = lead.last_status_date || lead.created_at;
  const trueDaysInColumn = lastStatus 
    ? Math.max(0, Math.floor((Date.now() - new Date(lastStatus).getTime()) / 86400000))
    : (lead.days_in_column || 0);

  return {
    id: lead.id,
    company: lead.company,
    contact: lead.contact_name ?? '',
    email: lead.email || '',
    phone: lead.phone || '',
    priority: (lead.priority || 'medium') as Deal['priority'],
    value: Number(lead.value) || 0,
    cashCollect: Number(lead.cash_collect) || 0,
    mrr: Number(lead.mrr) || 0,
    contractValue: Number(lead.contract_value) || 0,
    column: lead.column_id || 'prospeccao',
    daysInColumn: trueDaysInColumn,
    origin: lead.origin || '',
    service: lead.service || '',
    score: lead.score || 0,
    tags: lead.tags || [],
    notes: lead.notes || '',
    customFields: lead.custom_fields || {},
    timeline: lead.timeline || [],
    contactLogs: lead.contact_logs || [],
    createdAt: lead.created_at,
    lastStatusDate: lastStatus,
    lastContactAt: lead.last_contact_at ?? null,
    statusHistory: lead.status_history || [],
    responsible: lead.responsible || '',
    sdr: lead.sdr ?? undefined,
    closer: lead.closer ?? undefined,
    sdrId: lead.sdr_id ?? undefined,
    closerId: lead.closer_id ?? undefined,
    clientCategory: (lead.client_category as Deal['clientCategory']) ?? undefined,
    proposalAttached: lead.proposal_attached ?? undefined,
    proposalAttachments: lead.proposal_attachments || [],
    porte: (lead.porte as Deal['porte']) ?? undefined,
    contactAvatar: lead.contact_avatar ?? undefined,
    companyLogo: lead.company_logo ?? undefined,
    companyInfo: (lead.company_info as Deal['companyInfo']) ?? undefined,
    contactInfo: (lead.contact_info as Deal['contactInfo']) ?? undefined,
    additionalContacts: lead.additional_contacts || [],
    utm_source: lead.utm_source ?? undefined,
    utm_medium: lead.utm_medium ?? undefined,
    utm_content: lead.utm_content ?? undefined,
    utm_term: lead.utm_term ?? undefined,
    territory: (lead as any).territory ?? undefined,
    city: (lead as any).city ?? undefined,
    state: (lead as any).state ?? undefined,
    lostReason: (lead as any).lost_reason ?? null,
  };
}

// ─── Helper: Convert store Deal to DB lead format ───
export function dealToDbLead(deal: Partial<Deal>): Record<string, unknown> {
  const obj: Record<string, unknown> = {};
  if (deal.company !== undefined) obj.company = deal.company;
  if (deal.contact !== undefined) obj.contact_name = deal.contact;
  if (deal.email !== undefined) obj.email = deal.email;
  if (deal.phone !== undefined) obj.phone = deal.phone;
  if (deal.priority !== undefined) obj.priority = deal.priority;
  if (deal.value !== undefined) obj.value = deal.value;
  if (deal.cashCollect !== undefined) obj.cash_collect = deal.cashCollect;
  if (deal.mrr !== undefined) obj.mrr = deal.mrr;
  if (deal.contractValue !== undefined) obj.contract_value = deal.contractValue;
  if (deal.column !== undefined) obj.column_id = deal.column;
  if (deal.daysInColumn !== undefined) obj.days_in_column = deal.daysInColumn;
  if (deal.origin !== undefined) obj.origin = deal.origin;
  if (deal.service !== undefined) obj.service = deal.service;
  if (deal.score !== undefined) obj.score = deal.score;
  if (deal.tags !== undefined) obj.tags = deal.tags;
  if (deal.notes !== undefined) obj.notes = deal.notes;
  if (deal.customFields !== undefined) obj.custom_fields = deal.customFields;
  if (deal.timeline !== undefined) obj.timeline = deal.timeline;
  if (deal.contactLogs !== undefined) obj.contact_logs = deal.contactLogs;
  if (deal.statusHistory !== undefined) obj.status_history = deal.statusHistory;
  if (deal.responsible !== undefined) obj.responsible = deal.responsible;
  if (deal.sdr !== undefined) obj.sdr = deal.sdr;
  if (deal.closer !== undefined) obj.closer = deal.closer;
  // O trigger no banco deriva o nome do id (e vice-versa); mandar o id é o caminho novo.
  if (deal.sdrId !== undefined) obj.sdr_id = deal.sdrId;
  if (deal.closerId !== undefined) obj.closer_id = deal.closerId;
  if (deal.clientCategory !== undefined) obj.client_category = deal.clientCategory;
  if (deal.proposalAttached !== undefined) obj.proposal_attached = deal.proposalAttached;
  if (deal.proposalAttachments !== undefined) obj.proposal_attachments = deal.proposalAttachments;
  if (deal.porte !== undefined) obj.porte = deal.porte;
  if (deal.contactAvatar !== undefined) obj.contact_avatar = deal.contactAvatar;
  if (deal.companyLogo !== undefined) obj.company_logo = deal.companyLogo;
  if (deal.companyInfo !== undefined) obj.company_info = deal.companyInfo;
  if (deal.contactInfo !== undefined) obj.contact_info = deal.contactInfo;
  if (deal.additionalContacts !== undefined) obj.additional_contacts = deal.additionalContacts;
  if (deal.utm_source !== undefined) obj.utm_source = deal.utm_source;
  if (deal.utm_medium !== undefined) obj.utm_medium = deal.utm_medium;
  if (deal.utm_content !== undefined) obj.utm_content = deal.utm_content;
  if (deal.utm_term !== undefined) obj.utm_term = deal.utm_term;
  if (deal.territory !== undefined) obj.territory = deal.territory;
  if (deal.city !== undefined) obj.city = deal.city;
  if (deal.state !== undefined) obj.state = deal.state;
  if (deal.lastStatusDate !== undefined) obj.last_status_date = deal.lastStatusDate;
  if (deal.lostReason !== undefined) obj.lost_reason = deal.lostReason;

  // Sanitize: convert empty strings to null for nullable DB columns
  // (CHECK constraints on porte/client_category reject empty strings)
  const nullableStringFields = [
    'email', 'phone', 'origin', 'service', 'notes', 'responsible',
    'sdr', 'closer', 'client_category', 'porte',
    'contact_avatar', 'company_logo',
    'utm_source', 'utm_medium', 'utm_content', 'utm_term', 'territory',
    'city', 'state',
  ];
  for (const f of nullableStringFields) {
    if (f in obj && obj[f] === '') obj[f] = null;
  }

  return obj;
}
